import * as React from "react";

import {getGqlRequestId, toGqlOperation} from "@khanacademy/wonder-blocks-data";
import {documentNode, type GqlOperation} from "@khan/data-gql-core";

import type {GqlContext} from "./types.ts";
import {useGqlContext} from "./use-gql-context.ts";

/**
 * A hook to build requestIds for GQL operations.
 *
 * This incorporates combining context with defaults.
 */
export const useRequestIdBuilder = (): (<
    TData extends Record<any, any>,
    TVariables extends Record<any, any>,
>(
    operation: GqlOperation<TData, TVariables>,
    variables: TVariables | null | undefined,
    context: Partial<GqlContext>,
) => string) => {
    const contextCombiner = useGqlContext();

    const requestIdBuilder = React.useCallback(
        <TData extends Record<any, any>, TVariables extends Record<any, any>>(
            operation: GqlOperation<TData, TVariables>,
            variables?: TVariables | null,
            context?: Partial<GqlContext>,
        ) => {
            const wbdOperation = toGqlOperation(documentNode(operation));
            const combinedContext = contextCombiner(context);

            return getGqlRequestId(wbdOperation, variables, combinedContext);
        },
        [contextCombiner],
    );
    return requestIdBuilder;
};
